import { create } from 'zustand';

export interface NetworkState {
  online: boolean;
  inFlight: number;
  lastError: Error | null;
  lastSyncAt: number | null;
  setOnline: (online: boolean) => void;
  incInFlight: () => void;
  decInFlight: () => void;
  noteError: (e: Error | null) => void;
  noteSync: () => void;
  withTracking: <T>(promise: Promise<T>) => Promise<T>;
}

function detectOnline(): boolean {
  try {
    return typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
  } catch {
    return true;
  }
}

export const useNetworkStore = create<NetworkState>((set, get) => ({
  online: detectOnline(),
  inFlight: 0,
  lastError: null,
  lastSyncAt: null,
  setOnline: (online) => set({ online }),
  incInFlight: () => set((s) => ({ inFlight: s.inFlight + 1 })),
  decInFlight: () => set((s) => ({ inFlight: Math.max(0, s.inFlight - 1) })),
  noteError: (lastError) => set({ lastError }),
  noteSync: () => set({ lastSyncAt: Date.now() }),
  withTracking: async <T>(promise: Promise<T>): Promise<T> => {
    get().incInFlight();
    try {
      const value = await promise;
      get().noteSync();
      return value;
    } catch (e) {
      get().noteError(e instanceof Error ? e : new Error(String(e)));
      throw e;
    } finally {
      get().decInFlight();
    }
  }
}));

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('online', () => useNetworkStore.getState().setOnline(true));
  window.addEventListener('offline', () => useNetworkStore.getState().setOnline(false));
}
